import React, { useCallback, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import logo from '../../assets/header.png';
import './helpdesk/SuscripcionesT8.css';
import './EstadoCuentaT8.css';

function formatPrice(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return `$${num.toFixed(2)}`;
}

function statusBadge(status) {
  if (status === 'activo') return <span className="badge bg-success">Activo</span>;
  if (status === 'mora') return <span className="badge bg-warning text-dark">En mora</span>;
  if (status === 'suspendido') return <span className="badge bg-danger">Suspendido</span>;
  return <span className="badge bg-secondary">{status || '—'}</span>;
}

function cycleLabel(cycle) {
  return cycle === 'annual' ? 'Anual' : 'Mensual';
}

function serialFromUrl(pathSerial, searchParams) {
  const fromPath = pathSerial ? decodeURIComponent(pathSerial) : '';
  const fromQuery = searchParams.get('serial') || '';
  return (fromPath || fromQuery).trim();
}

function EstadoCuentaT8() {
  const { serial: pathSerial } = useParams();
  const [searchParams] = useSearchParams();
  const urlSerial = serialFromUrl(pathSerial, searchParams);
  const [loading, setLoading] = useState(Boolean(urlSerial));
  const [errorMessage, setErrorMessage] = useState('');
  const [statement, setStatement] = useState(null);
  const [empty, setEmpty] = useState(false);

  const lookup = useCallback(async (rawSerial) => {
    const trimmed = String(rawSerial || '').trim();
    if (!trimmed) return;

    setLoading(true);
    setErrorMessage('');
    setStatement(null);
    setEmpty(false);

    try {
      const data = new FormData();
      data.append('serial', trimmed);
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}t8_subscribers/public_statement.php`, {
        method: 'POST',
        body: data,
      });
      const text = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        setErrorMessage('No se pudo consultar el estado de cuenta.');
        return;
      }
      if (!response.ok || parsed?.error) {
        setErrorMessage(parsed?.message || 'No se pudo consultar el estado de cuenta.');
        return;
      }
      if (!parsed?.has_subscription || !parsed?.statement) {
        setEmpty(true);
        return;
      }
      setStatement(parsed.statement);
    } catch {
      setErrorMessage('No se pudo consultar el estado de cuenta.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!urlSerial) {
      setLoading(false);
      setErrorMessage('');
      setStatement(null);
      setEmpty(false);
      return;
    }
    lookup(urlSerial);
  }, [urlSerial, lookup]);

  return (
    <div className="estado-cuenta-page">
      <div className="estado-cuenta-inner">
        <img src={logo} alt="TOMi" className="estado-cuenta-logo" />
        <h1 className="suscripciones-t8-title">Estado de cuenta</h1>
        <p className="suscripciones-t8-subtitle">Suscripción de tu TOMi 8</p>

        {!urlSerial && (
          <div className="alert alert-info mt-4 text-start mb-0">
            Esta página necesita el serial en la dirección.
          </div>
        )}

        {urlSerial && loading && (
          <p className="text-muted mt-4 mb-0">Consultando…</p>
        )}

        {urlSerial && !loading && errorMessage && (
          <div className="alert alert-warning mt-4 text-start mb-0">{errorMessage}</div>
        )}

        {urlSerial && !loading && empty && (
          <div className="alert alert-info mt-4 text-start mb-0">
            Este serial no tiene una suscripción TOMi 8.
          </div>
        )}

        {urlSerial && !loading && statement && (
          <div className="text-start mt-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h5 className="card-title mb-0">Suscripción TOMi 8</h5>
                  {statusBadge(statement.status)}
                </div>
                <dl className="row mb-0 suscripciones-t8-dl">
                  <dt className="col-sm-5">Serial</dt>
                  <dd className="col-sm-7 font-monospace">{statement.serial_num}</dd>
                  <dt className="col-sm-5">Plan</dt>
                  <dd className="col-sm-7">{statement.plan_name || '—'}</dd>
                  <dt className="col-sm-5">Ciclo</dt>
                  <dd className="col-sm-7">{cycleLabel(statement.billing_cycle)}</dd>
                  <dt className="col-sm-5">Próximo vencimiento</dt>
                  <dd className="col-sm-7">{statement.next_due_date || '—'}</dd>
                  <dt className="col-sm-5">Precio</dt>
                  <dd className="col-sm-7 mb-0">{formatPrice(statement.price)}</dd>
                </dl>
              </div>
            </div>

            <div className="card shadow-sm mt-3">
              <div className="card-body">
                <h5 className="card-title">Pagos</h5>
                {(statement.payments || []).length === 0 ? (
                  <p className="text-muted mb-0">Sin pagos registrados.</p>
                ) : (
                  <Table bordered hover size="sm" responsive className="mb-0 bg-white">
                    <thead className="table-light">
                      <tr>
                        <th>Factura</th>
                        <th>Monto</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.payments.map((pay, index) => (
                        <tr key={`${pay.invoice_number}-${pay.paid_at}-${index}`}>
                          <td>{pay.invoice_number || '—'}</td>
                          <td>{formatPrice(pay.amount)}</td>
                          <td>{pay.paid_at || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EstadoCuentaT8;
